import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'shared/components/UC/Button';
import BackLink from '../components/BackLink';
import CriteriaScoresSection from '../components/evaluation/CriteriaScoresSection';
import EvaluationDetailsGrid from '../components/evaluation/EvaluationDetailsGrid';
import EvaluationInfoTable from '../components/evaluation/EvaluationInfoTable';
import adminService from '../services/adminService';
import { SingleEvaluationResponseDto } from '../types/admin';
import styles from './AdminEvaluationSummary.module.css';

const AdminEvaluationSummary: React.FC = () => {
  const { evaluationId } = useParams<{ evaluationId: string }>();
  const navigate = useNavigate();
  const [evaluationData, setEvaluationData] =
    useState<SingleEvaluationResponseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEvaluationData = useCallback(async () => {
    if (!evaluationId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getEvaluationSummary(evaluationId);
      setEvaluationData(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Error al cargar las evaluaciones'
      );
    } finally {
      setLoading(false);
    }
  }, [evaluationId]);

  useEffect(() => {
    if (evaluationId) {
      loadEvaluationData();
    } else {
      setError('ID de evaluación no proporcionado');
      setLoading(false);
    }
  }, [evaluationId, loadEvaluationData]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <h2>Cargando resumen de evaluación...</h2>
          <p>Por favor espere mientras se cargan los datos.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Error al cargar la evaluación</h2>
          <p>{error}</p>
          <div className={styles.errorActions}>
            <Button variant="primary" onClick={loadEvaluationData}>
              Reintentar
            </Button>
            <Button variant="secondary" onClick={handleBack}>
              Volver
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!evaluationData) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Evaluación no encontrada</h2>
          <p>No se encontraron datos de evaluación para el ID proporcionado.</p>
          <Button variant="primary" onClick={handleBack}>
            Volver
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.backButton}>
            <BackLink onClick={handleBack} />
          </div>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>Resumen de Evaluación</h1>
            <div className={styles.applicationInfo}>
              <span className={styles.applicationNumber}>
                Folio: {evaluationData.application.applicationNumber}
              </span>
              <span className={styles.separator}>•</span>
              <span className={styles.evaluatorName}>
                Evaluador: {evaluationData.evaluator.name}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Evaluation Overview */}
      <EvaluationInfoTable
        evaluation={evaluationData}
        finalScore={evaluationData.scoreSummary.finalScore}
        showEvaluatorInfo={true}
        evaluatorName={evaluationData.evaluator.name}
        isExternal={evaluationData.evaluator.isExternal}
        assignedAt={evaluationData.assignment.assignedAt}
      />

      {/* Criteria Scores */}
      <CriteriaScoresSection
        criteriaEvaluations={evaluationData.criteriaEvaluations}
        showComments={true}
      />

      {/* Additional Details */}
      <EvaluationDetailsGrid
        evaluation={evaluationData}
        showEvaluatorInfo={true}
        evaluatorName={evaluationData.evaluator.name}
        evaluatorEmail={evaluationData.evaluator.email}
        isExternal={evaluationData.evaluator.isExternal}
        assignedAt={evaluationData.assignment.assignedAt}
        assignmentStatus={evaluationData.assignment.status}
      />

      {/* Footer */}
      <div className={styles.footer}>
        <Button variant="primary" onClick={handleBack}>
          Volver al Panel de Administración
        </Button>
      </div>
    </div>
  );
};

export default AdminEvaluationSummary;
